import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useGetAttachmentTypesApi } from 'hooks/api/useAttachments';
import {
  useCreateTicketAttachment,
  useCreateTicketMultiAttachment,
  useCreateTicketMultiPartAttachmentComplete,
  useCreateTicketMultiPartAttachmentInitiate,
  useCreateTicketMultiPartAttachmentUpload,
} from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import type { TicketAttachmentFormValuesT } from '../declarations';

type UseAddAttachmentPopupT = {
  handleSubmitForm: (data: TicketAttachmentFormValuesT, onClose: NoopT) => Promise<void>;
  isCreatingAttachment: boolean;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
  attachmentTypesOptions: SelectOptionT[];
  isLoadingAttachmentTypesOptions: boolean;
};

type UseAddAttachmentPopupParamsT = {
  ticketId: string;
  categoryName: string;
  assignToWarrCloud: boolean;
};

const useAddAttachmentPopup = ({
  ticketId,
  categoryName,
  assignToWarrCloud,
}: UseAddAttachmentPopupParamsT): UseAddAttachmentPopupT => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const attachmentTypeResult = useGetAttachmentTypesApi();

  const createAttachmentMutation = useCreateTicketAttachment({
    ticketId,
    onSuccess: (_data, variables, _context) => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);

      if (variables.AssignToOppositeUser) {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      }
    },
  });

  const createMultiAttachmentMutation = useCreateTicketMultiAttachment({
    ticketId,
    onSuccess: (_data, variables, _context) => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);

      if (variables[0].AssignToOppositeUser) {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      }
    },
  });

  const createMultiPartAttachmentInitiateMutation = useCreateTicketMultiPartAttachmentInitiate({
    ticketId,
    onSuccess: (_data, _context) => {},
  });

  const createMultiPartAttachmentUploadMutation = useCreateTicketMultiPartAttachmentUpload({
    onSuccess: (_data, _context) => {},
  });

  const createMultiPartAttachmentCompleteMutation = useCreateTicketMultiPartAttachmentComplete({
    ticketId,
    onSuccess: (_data, _context) => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  const attachmentTypesOptions = useMemo(
    () =>
      attachmentTypeResult.data
        ? attachmentTypeResult.data?.map(({ attachmentTypeCode, attachmentTypeDescription }) => ({
            label: attachmentTypeDescription,
            value: attachmentTypeCode,
          }))
        : [],
    [attachmentTypeResult.data]
  );

  const createChunks = (file: File, cSize: number): Blob[] => {
    let startPointer = 0;
    const endPointer = file.size;
    const chunks = [];
    while (startPointer < endPointer) {
      const newStartPointer = startPointer + cSize;
      chunks.push(file.slice(startPointer, newStartPointer));
      startPointer = newStartPointer;
    }
    return chunks;
  };

  async function blobToBase64Async(blob: Blob): Promise<string> {
    return new Promise(
      (resolve: (value: string) => void, reject: (reason?: Error) => void): void => {
        const fileReader = new FileReader();
        fileReader.onerror = (_e): void => {
          if (fileReader.error) {
            reject(
              fileReader.error instanceof Error
                ? fileReader.error
                : new Error(String(fileReader.error))
            );
          } else {
            reject(new Error('FileReader error'));
          }
        };
        fileReader.onloadend = (_e): void => {
          const dataUrl = fileReader.result as string;
          const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
          resolve(base64);
        };
        fileReader.readAsDataURL(blob);
      }
    );
  }

  const resetCreateAttachmentMutationState = useCallback(() => {
    if (createAttachmentMutation.isSuccess || createAttachmentMutation.isError) {
      createAttachmentMutation.reset();
    }
  }, [createAttachmentMutation]);

  const resetCreateMultiAttachmentMutationState = useCallback(() => {
    if (createMultiAttachmentMutation.isSuccess || createMultiAttachmentMutation.isError) {
      createMultiAttachmentMutation.reset();
    }
  }, [createMultiAttachmentMutation]);

  const resetCreateMultiPartAttachmentInitiateMutationState = useCallback(() => {
    if (
      createMultiPartAttachmentInitiateMutation.isSuccess ||
      createMultiPartAttachmentInitiateMutation.isError
    ) {
      createMultiPartAttachmentInitiateMutation.reset();
    }
  }, [createMultiPartAttachmentInitiateMutation]);

  const resetCreateMultiPartAttachmentCompleteMutationState = useCallback(() => {
    if (
      createMultiPartAttachmentCompleteMutation.isSuccess ||
      createMultiPartAttachmentCompleteMutation.isError
    ) {
      createMultiPartAttachmentCompleteMutation.reset();
    }
  }, [createMultiPartAttachmentCompleteMutation]);

  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetCreateAttachmentMutationState();
      resetCreateMultiPartAttachmentCompleteMutationState();
      resetCreateMultiAttachmentMutationState();
      resetCreateMultiPartAttachmentInitiateMutationState();
    },
    [
      resetCreateAttachmentMutationState,
      resetCreateMultiAttachmentMutationState,
      resetCreateMultiPartAttachmentInitiateMutationState,
      resetCreateMultiPartAttachmentCompleteMutationState,
    ]
  );

  const handleSubmitForm = useCallback(
    async (data: TicketAttachmentFormValuesT, onClose: NoopT) => {
      const attachmentPhoto = data.attachmentPhoto || [];
      const attachmentFile = data.attachmentFile || [];

      const attachmentPhotoArray = Array.from(attachmentPhoto) as File[];
      const attachmentFileArray = Array.from(attachmentFile) as File[];

      const allAttachments = [...attachmentPhotoArray, ...attachmentFileArray];

      try {
        for (const attachment of allAttachments) {
          if (attachment.size < 10000000) {
            await createAttachmentMutation.mutateAsync({
              'Attachment.File': attachment,
              'Attachment.AttachmentTypeCode': data.attachmentTypeCode,
              'Attachment.Notes': data.note || categoryName,
              AssignToOppositeUser: assignToWarrCloud,
            });

            continue;
          }

          const initiateResponse = await createMultiPartAttachmentInitiateMutation.mutateAsync({
            attachmentTypeCode: data.attachmentTypeCode,
            fileContentType: attachment.type,
          });

          const chunks = createChunks(attachment, 6000000);
          let partCounter = 1;
          const partNumberETags = [];

          for (const chunk of chunks) {
            const content = await blobToBase64Async(chunk);

            const uploadResponse = await createMultiPartAttachmentUploadMutation.mutateAsync({
              fileKey: initiateResponse.fileKey,
              uploadId: initiateResponse.uploadId,
              partNumber: partCounter,
              content: content,
            });

            partCounter++;
            partNumberETags.push(uploadResponse);
          }

          await createMultiPartAttachmentCompleteMutation.mutateAsync({
            fileKey: initiateResponse.fileKey,
            uploadId: initiateResponse.uploadId,
            partNumberETags: partNumberETags,
            attachmentTypeCode: data.attachmentTypeCode,
            notes: data.note || categoryName,
            assignToOppositeUser: assignToWarrCloud,
            originalFileName: attachment.name,
          });
        }

        dispatch(
          updateSnackbarNotification({
            snackbarMessage: 'Attachment successfully published',
            snackBarSeverity: 'success',
          })
        );
        onClose();
        resetCreateAttachmentMutationState();
        resetCreateMultiAttachmentMutationState();
        resetCreateMultiPartAttachmentInitiateMutationState();
        resetCreateMultiPartAttachmentCompleteMutationState();
      } catch (error) {
        const errorMsg =
          getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
        dispatch(
          updateSnackbarNotification({
            snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
            snackBarSeverity: 'error',
          })
        );
        onClose();
      }
    },
    [
      categoryName,
      assignToWarrCloud,
      createAttachmentMutation,
      createMultiPartAttachmentInitiateMutation,
      createMultiPartAttachmentUploadMutation,
      createMultiPartAttachmentCompleteMutation,
      dispatch,
      resetCreateAttachmentMutationState,
      resetCreateMultiAttachmentMutationState,
      resetCreateMultiPartAttachmentInitiateMutationState,
      resetCreateMultiPartAttachmentCompleteMutationState,
    ]
  );

  return {
    handleSubmitForm,
    isCreatingAttachment:
      createAttachmentMutation.isLoading ||
      createMultiPartAttachmentInitiateMutation.isLoading ||
      createMultiPartAttachmentUploadMutation.isLoading ||
      createMultiPartAttachmentCompleteMutation.isLoading,
    handleClosePopup,
    attachmentTypesOptions,
    isLoadingAttachmentTypesOptions: attachmentTypeResult.isLoading,
  };
};

export default useAddAttachmentPopup;
